import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  X,
  Minus,
  Maximize2,
  Paperclip,
  Trash2,
  Image,
  Send,
  Laugh,
} from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import TextEditorMail from "./TextEditorMail";
import { toast } from "react-toastify";
import { fetchJson } from "@utils/fetchJson";
import { useEmailCreateStore } from "./store/useMailStore";

export default function Mail() {
  const [isMinimized, setMinimized] = useState(false);
  const [isMaximized, setMaximized] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const { isOpen,payload: payloadWrapper, close } = useEmailCreateStore();
  const [formData, setFormData] = useState({
    to: "",
    cc: "",
    bcc: "",
    subject: "",
    html: "",
    attachments: [],
  });

  const fileInput = useRef(null);
  const imgInput = useRef(null);
  const editorRef = useRef(null);
  const bodyRef = useRef(null);
  const footerRef = useRef(null);

  const reset = () => {
    setFormData({
      to: "",
      cc: "",
      bcc: "",
      subject: "",
      html: "",
      attachments: [],
    });
    close();
  }
  const insertImage = (file) => {
    if (!file || !editorRef.current) return;
    const reader = new FileReader();
    reader.onload = () => {
      editorRef.current.chain().focus().setImage({ src: reader.result }).run();
      if (imgInput.current) imgInput.current.value = "";
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files || []);
    if (!files.length) return;

    const y = e.clientY;
    const bodyRect = bodyRef.current?.getBoundingClientRect();
    const footerRect = footerRef.current?.getBoundingClientRect();

    files.forEach((file) => {
      const isOverBody = bodyRect && y >= bodyRect.top && y <= bodyRect.bottom;
      const isOverFooter = footerRect && y >= footerRect.top;

      if (isOverBody && file.type.startsWith("image/")) {
        insertImage(file);
      } else if (isOverFooter || !isOverBody) {
        setFormData((prev) => ({
          ...prev,
          attachments: [...prev.attachments, file],
        }));
      }
    });
  }, []);

  useEffect(() => {
    const hide = (e) => {
      const el = document.querySelector(".EmojiPickerReact");
      if (el && !el.contains(e.target)) setShowEmoji(false);
    };
    if (showEmoji) document.addEventListener("mousedown", hide);
    return () => document.removeEventListener("mousedown", hide);
  }, [showEmoji]);

  const renderLabeledInput = (
    field,
    label,
    showClose = false,
    closeFn = () => {console.log("close");}
  ) => (
    <div className="flex items-center border-b px-4 py-2">
      <label className="text-sm text-gray-700">{label}:</label>
      <input
        type="text"
        name={field}
        value={formData[field]}
        onChange={(e) => setFormData({ ...formData, [field]: e.target.value })}
        className="flex-1 ml-2 outline-none text-sm bg-transparent"
      />
      {showClose && (
        <button onClick={close()} className="ml-2 text-gray-400 hover:text-gray-600"> <X className="w-3 h-3" /></button>
      )}
    </div>
  );
  // const handleSend = async () => {
  //   const payload = {
  //     toField: formData.to.split(",").map((s) => s.trim()),
  //     subject: formData.subject,
  //     mail_description: formData.html,
  //     fromField: "rushikesh.raval@mkvisolutions.com",
  //     attachmentArray: JSON.stringify([]), 
  //   };

  //   const res = await fetchJson("https://cms.coachgenie.in/API/emailSend", {
  //     method: "POST",
  //     body: JSON.stringify(payload), // Send flat JSON
  //   });

  //   if (res?.flag === "T") {
  //     toast.success("Mail sent successfully!");
  //     reset();
  //   } else {
  //     toast.error(res?.msg || " Failed to send mail.");
  //   }
  // };
  const handleSend = async () => {
    const payload = {
      toField: formData.to.split(",").map((s) => s.trim()),
      subject: formData.subject,
      mail_description: formData.html,
      fromField: "rushikesh.raval@mkvisolutions.com",
      attachmentArray: JSON.stringify([]), 
    };

    const res = await fetchJson("https://cms.coachgenie.in/API/emailSend", {
      method: "POST",
      body: JSON.stringify(payload),
    });

    if (res?.flag === "T") {
      toast.success(" Mail sent successfully!");
      reset();
    } else {
      toast.error(res?.msg || " Failed to send mail.");
    }
  };
  if (!isOpen) return null;
  return (
    <div
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`z-50 bg-white border shadow-lg flex flex-col rounded-md overflow-hidden transition-all fixed ${
        isMaximized
          ? "top-1/2 right-1/2 -translate-y-1/2 translate-x-1/2 w-[82vw] h-[85vh] sm:w-[76vw] sm:h-[84vh]"
          : isMinimized
          ? "w-[90vw] h-[40px] bottom-4 right-4 sm:w-[340px]"
          : "w-[96vw] h-[85vh] bottom-4 right-4 sm:w-[620px] sm:h-[540px]"
      }`}
      style={{ fontFamily: "Roboto, sans-serif", fontSize: "13px" }}
    >
      <div className="flex justify-between items-center px-4 h-10 bg-gray-100 border-b">
        <span className="font-medium">New Message</span>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setMinimized(!isMinimized);
              if (!isMinimized) setMaximized(false);
            }}
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setMaximized(!isMaximized);
              setMinimized(false);
            }}
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button onClick={()=>{reset()}}>
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          <div className="flex flex-col flex-1 overflow-hidden">
            <div className="flex items-center border-b px-4 py-2 relative">
              <label className="text-sm text-gray-700">To:</label>
              <input
                type="text"
                name="to"
                value={formData.to}
                onChange={(e) =>
                  setFormData({ ...formData, to: e.target.value })
                }
                className="flex-1 ml-2 outline-none text-sm bg-transparent"
              />
              <div className="absolute right-4 flex gap-2 text-blue-600 text-xs">
                {!showCc && (
                  <button
                    onClick={() => setShowCc(true)}
                    className="hover:underline"
                  >
                    Cc
                  </button>
                )}
                {!showBcc && (
                  <button
                    onClick={() => setShowBcc(true)}
                    className="hover:underline"
                  >
                    Bcc
                  </button>
                )}
              </div>
            </div>
            {showCc &&
              renderLabeledInput("cc", "Cc", true, () => setShowCc(false))}
            {showBcc &&
              renderLabeledInput("bcc", "Bcc", true, () => setShowBcc(false))}
            {renderLabeledInput("subject", "Subject")}

            <div ref={bodyRef} className="flex-1 overflow-hidden">
              <TextEditorMail
                value={formData.html}
                onChange={(html) => setFormData({ ...formData, html })}
                editorRef={editorRef}
              />
            </div>

            {formData.attachments.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 py-2 bg-gray-50 border-t">
                {formData.attachments.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center bg-gray-100 px-2 py-1 rounded shadow-sm text-sm"
                  >
                    <span className="truncate max-w-[120px]">{f.name}</span>
                    <button
                      className="ml-2"
                      onClick={() =>
                        setFormData((p) => ({
                          ...p,
                          attachments: p.attachments.filter(
                            (_, idx) => idx !== i
                          ),
                        }))
                      }
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div
            ref={footerRef}
            className="flex justify-between items-center px-4 py-2 border-t bg-gray-50 flex-wrap gap-y-2"
          >
            <div className="flex items-center space-x-3 h-full">
              <button
                onClick={handleSend}
                className="flex items-center gap-1 bg-[#0b57d0] hover:bg-blue-700 text-white text-sm font-medium px-4 py-1.5 rounded-3xl"
              >
                <Send className="w-4 h-4" /> Send
              </button>

              <input
                ref={fileInput}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setFormData((p) => ({
                      ...p,
                      attachments: [...p.attachments, file],
                    }));
                  }
                }}
              />
              <button
                className="flex items-center justify-center h-8 w-8"
                onClick={() => fileInput.current?.click()}
              >
                <Paperclip className="w-4 h-4" />
              </button>

              <input
                ref={imgInput}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => insertImage(e.target.files?.[0])}
              />
              <button
                className="flex items-center justify-center h-8 w-8"
                onClick={() => imgInput.current?.click()}
              >
                <Image className="w-4 h-4" />
              </button>

              <div className="relative">
                <button
                  className="flex items-center justify-center h-8 w-8"
                  onClick={() => setShowEmoji((p) => !p)}
                >
                  <Laugh className="w-4 h-4" />
                </button>
                {showEmoji && (
                  <div className="absolute bottom-10 left-0 z-50">
                    <EmojiPicker
                      onEmojiClick={(e) => {
                        if (editorRef.current) {
                          editorRef.current
                            .chain()
                            .focus()
                            .insertContent(e.emoji)
                            .run();
                        }
                      }}
                      height={350}
                      width={300}
                    />
                  </div>
                )}
              </div>
            </div>

            <button onClick={reset}>
              <Trash2 className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
